import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import bgimg from "../../assets/register.png";
import logo from "../../assets/Easymarg Whitebg.png";
import axiosInstance from "../../api/axiosInstance";
import {
  FaUser, FaPhone, FaEnvelope, FaBuilding, FaMapMarkerAlt,
  FaLock, FaEye, FaEyeSlash, FaShieldAlt, FaRedo,
  FaListUl, FaChevronDown, FaCheck, FaCheckCircle
} from "react-icons/fa";

const Register = () => {
  const navigate = useNavigate();

  const [userType, setUserType] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [phoneno, setPhoneno] = useState("");
  const [emailid, setEmailid] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // OTP Verification States
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (showOtpModal && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [showOtpModal, countdown]);

  const handleEmailChange = (e) => {
    const value = e.target.value.toLowerCase();
    setEmailid(value);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhoneno(value);
  };

  const validateForm = () => {
    if (!userType) {
      setError("Please select User Type");
      return false;
    }
    if (!username.trim()) {
      setError("Username is required");
      return false;
    }
    if (phoneno.length !== 10) {
      setError("Phone number must be 10 digits");
      return false;
    }
    if (!/^([6-9])\d{9}$/.test(phoneno)) {
      setError("Phone must start with 6-9");
      return false;
    }
    if (!emailid || !/^[a-z][a-z0-9._%+-]*@[a-z0-9.-]+\.[a-z]{2,}$/.test(emailid)) {
      setError("Enter valid lowercase email (e.g. name@example.com)");
      return false;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    if (!acceptedTerms) {
      setError("Please accept the Terms and Privacy Policy to create an account");
      return false;
    }
    if (userType === "Corporate Companies" && (!companyName.trim() || !companyAddress.trim())) {
      setError("Company Name and Address are required");
      return false;
    }
    return true;
  };

  const handleInitiateRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Send OTP to email
      await axiosInstance.post("/users/send-otp", {
        email: emailid,
        username: username.trim()
      });
      setOtp("");
      setError("");
      setCountdown(60);
      setCanResend(false);
      setShowOtpModal(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || resendLoading) return;
    setResendLoading(true);
    setError("");
    try {
      await axiosInstance.post("/users/send-otp", {
        email: emailid,
        username: username.trim()
      });
      setCountdown(60);
      setCanResend(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend code");
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      return setError("Please enter the 6-digit verification code");
    }
    if (otp.trim().length !== 6) {
      return setError("Verification code must be 6 digits");
    }

    const payload = {
      userType,
      username: username.trim(),
      phoneno,
      emailid,
      password,
      otp: otp.trim(),
      ...(userType === "Corporate Companies" && {
        companyName: companyName.trim(),
        companyAddress: companyAddress.trim()
      }),
    };

    setOtpLoading(true);
    setError("");
    try {
      const response = await axiosInstance.post("/users/register", payload);
      
      // Auto-login if backend returns token
      if (response.data && response.data.token && response.data.user) {
          const { token, user } = response.data;
          const displayName = user.username || "User Account";
          localStorage.setItem('token', token);
          localStorage.setItem("userId", user.id);
          localStorage.setItem("userName", displayName);
          if (user.profilePic) localStorage.setItem("userPic", user.profilePic);
          window.dispatchEvent(new Event("authChange"));
      }

      setShowOtpModal(false);
      setSuccessMessage("Account registered successfully! Redirecting to Dashboard...");
      setTimeout(() => {
        navigate("/home", { replace: true });
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please check the code.");
    } finally {
      setOtpLoading(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <div className="h-screen w-full flex flex-col lg:flex-row overflow-hidden">

        {/* Left Brand Panel */}
        <div
            className="hidden lg:block relative w-full lg:w-1/2 lg:h-full shrink-0 bg-cover bg-center overflow-hidden"
            style={{ backgroundImage: `url(${bgimg})` }}
        >
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a2463]/95 via-[#134DA7]/90 to-[#0a2463]/95 backdrop-blur-sm"></div>

            <div className="absolute top-5 left-5 sm:top-8 sm:left-8 z-20 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2.5 shadow-lg">
                <img src={logo} alt="EasyMarg Logo" className="h-6 sm:h-7" />
            </div>

            <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="relative z-10 h-full flex flex-col items-center lg:items-start justify-center text-white text-center lg:text-left px-6 sm:px-10 lg:pl-16 lg:pr-10 xl:pl-24"
            >
                <motion.h1 variants={fadeInUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-3 lg:mb-6">
                    WELCOME <br />
                    <span className="font-light">TO </span>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-200">
                        EASYMARG
                    </span>
                </motion.h1>

                <motion.p variants={fadeInUp} className="text-sm sm:text-lg font-light text-blue-100/90 max-w-lg mx-auto lg:mx-0">
                    EasyMarg is your trusted partner in navigating the complexities of financial management.
                </motion.p>
            </motion.div>
        </div>

        {/* Right Form Panel */}
        <div className="flex-1 min-h-0 flex flex-col items-center bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="lg:hidden mb-6 mt-4 bg-white rounded-2xl px-5 py-3 shadow-sm border border-gray-100">
                <img src={logo} alt="EasyMarg Logo" className="h-7" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="w-full max-w-[480px] max-h-full my-auto flex flex-col min-h-0"
            >
                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-2 px-1 shrink-0">
                    <span>EasyMarg</span>
                    <span className="text-gray-300">/</span>
                    <span className="text-[#134DA7] font-semibold">Create Account</span>
                </div>
                <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col min-h-0">
                    <div className="h-1.5 bg-gradient-to-r from-[#134DA7] to-[#66BBFC] shrink-0"></div>
                    <div className="auth-scrollbar flex-1 min-h-0 overflow-y-auto p-6 sm:p-8">
                        {error && <div className="mb-3 p-3 bg-red-50 text-red-600 rounded-lg text-sm text-center border border-red-100">{error}</div>}

                        {!showOtpModal ? (
                            <form onSubmit={handleInitiateRegister} className="space-y-3">
                                <div className="text-center mb-4">
                                    <h2 className="text-2xl font-bold text-[#0a2463]">Create Account</h2>
                                    <p className="text-sm text-gray-500 mt-1">Fill in your details to get started</p>
                                </div>
                                {/* User Type */}
                                <div className="space-y-1 relative z-30">
                                    <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider">User Type</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                                            <FaListUl className={`text-gray-400 transition-colors ${isDropdownOpen ? 'text-[#134DA7]' : 'group-focus-within:text-[#134DA7]'}`} />
                                        </div>
                                        
                                        {/* Custom Select Button */}
                                        <div 
                                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                            className={`w-full pl-11 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium transition-all cursor-pointer flex items-center ${isDropdownOpen ? 'border-[#134DA7] bg-white' : ''} ${!userType ? 'text-gray-500' : 'text-gray-700'}`}
                                        >
                                            {userType || "Select User Type"}
                                        </div>

                                        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none z-10">
                                            <FaChevronDown className={`text-gray-400 text-xs transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                        </div>

                                        {/* Dropdown Menu */}
                                        {isDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)}></div>
                                                <div className="absolute z-20 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden py-1">
                                                    {["Individual User", "Corporate Companies"].map((type) => (
                                                        <div
                                                            key={type}
                                                            onClick={() => {
                                                                setUserType(type);
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className={`px-4 py-3 text-sm cursor-pointer hover:bg-blue-50 hover:text-[#134DA7] transition-colors ${userType === type ? 'bg-blue-50 text-[#134DA7] font-semibold' : 'text-gray-700 font-medium'}`}
                                                        >
                                                            {type}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Full Name & Phone Number */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider">Full Name</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <FaUser className="text-gray-400 group-focus-within:text-[#134DA7]" />
                                            </div>
                                            <input
                                                type="text"
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium focus:border-[#134DA7] focus:bg-white transition-all"
                                                placeholder="Enter your name"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider">Phone Number</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <FaPhone className="text-gray-400 group-focus-within:text-[#134DA7]" />
                                            </div>
                                            <input
                                                type="tel"
                                                value={phoneno}
                                                onChange={handlePhoneChange}
                                                maxLength="10"
                                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium focus:border-[#134DA7] focus:bg-white transition-all"
                                                placeholder="10-digit mobile number"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Corporate Fields */}
                                {userType === "Corporate Companies" && (
                                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider">Company Name</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <FaBuilding className="text-gray-400 group-focus-within:text-[#134DA7]" />
                                            </div>
                                            <input
                                                type="text"
                                                value={companyName}
                                                onChange={(e) => setCompanyName(e.target.value)}
                                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium focus:border-[#134DA7] focus:bg-white transition-all"
                                                placeholder="Company name"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider">Company Address</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <FaMapMarkerAlt className="text-gray-400 group-focus-within:text-[#134DA7]" />
                                            </div>
                                            <input
                                                type="text"
                                                value={companyAddress}
                                                onChange={(e) => setCompanyAddress(e.target.value)}
                                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium focus:border-[#134DA7] focus:bg-white transition-all"
                                                placeholder="Company address"
                                            />
                                        </div>
                                    </div>
                                  </motion.div>
                                )}

                                {/* Email Address */}
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider">Email Address</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <FaEnvelope className="text-gray-400 group-focus-within:text-[#134DA7]" />
                                        </div>
                                        <input
                                            type="email"
                                            value={emailid}
                                            onChange={handleEmailChange}
                                            className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium focus:border-[#134DA7] focus:bg-white transition-all"
                                            placeholder="name@example.com"
                                        />
                                    </div>
                                </div>

                                {/* Password & Confirm Password Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Password */}
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider">Password (min 8)</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <FaLock className="text-gray-400 group-focus-within:text-[#134DA7]" />
                                            </div>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                maxLength="50"
                                                className="w-full pl-11 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium focus:border-[#134DA7] focus:bg-white transition-all"
                                                placeholder="Password"
                                            />
                                            <button 
                                                type="button" 
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#134DA7] transition-colors"
                                            >
                                                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider">Confirm</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <FaLock className="text-gray-400 group-focus-within:text-[#134DA7]" />
                                            </div>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="w-full pl-11 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium focus:border-[#134DA7] focus:bg-white transition-all"
                                                placeholder="Confirm"
                                            />
                                            <button 
                                                type="button" 
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#134DA7] transition-colors"
                                            >
                                                {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <label className="flex items-center gap-2 cursor-pointer group mb-4 pl-1">
                                        <div className="relative flex items-center justify-center">
                                            <input 
                                                type="checkbox" 
                                                checked={acceptedTerms}
                                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                                className="peer appearance-none w-4 h-4 border-2 border-gray-300 rounded cursor-pointer checked:bg-[#134DA7] checked:border-[#134DA7] transition-all shrink-0"
                                            />
                                            <FaCheck size={10} className="absolute text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                                        </div>
                                        <span className="text-[12px] text-gray-500 font-medium leading-tight select-none pt-[1px]">
                                            I agree to the <a href="https://easymarg.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#134DA7] hover:underline font-bold" onClick={(e) => e.stopPropagation()}>Terms</a> and <a href="https://easymarg.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#134DA7] hover:underline font-bold" onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
                                        </span>
                                    </label>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-2.5 text-white font-bold tracking-wide bg-[#134DA7] hover:bg-[#0f3d85] rounded-xl shadow-lg hover:shadow-2xl active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {loading ? (
                                          <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Sending Verification Code...</span>
                                          </>
                                        ) : (
                                          "Verify"
                                        )}
                                    </button>
                                </div>

                                <div className="pt-4 text-center text-[14px]">
                                    <p className="text-gray-600 font-medium">Already have an account? <Link to="/" className="text-[#134DA7] font-bold underline hover:text-[#0f3d85]">Login</Link></p>
                                </div>
                            </form>
                        ) : (
                            <motion.div 
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="w-full pt-4"
                            >
                                <div className="text-center mb-6">
                                  <div className="w-16 h-16 bg-blue-50 text-[#134DA7] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                                    <FaShieldAlt size={32} />
                                  </div>
                                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Verify Your Email</h3>
                                  <p className="text-sm text-gray-500">
                                    We sent a 6-digit verification code to
                                  </p>
                                  <p className="text-sm font-semibold text-[#134DA7] mt-0.5 break-all">
                                    {emailid}
                                  </p>
                                </div>

                                <form onSubmit={handleVerifyAndRegister} className="space-y-5">
                                  <div>
                                    <label className="text-xs font-semibold text-gray-500 ml-1 uppercase tracking-wider block mb-2 text-center">
                                      Enter 6-Digit Code
                                    </label>
                                    <input
                                      type="text"
                                      maxLength="6"
                                      value={otp}
                                      autoFocus
                                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                      placeholder="• • • • • •"
                                      className="w-full text-center tracking-[12px] text-2xl font-bold py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl outline-none focus:border-[#134DA7] focus:bg-white transition-all text-gray-900"
                                    />
                                  </div>

                                  <div className="flex items-center justify-between text-xs font-medium text-gray-500 px-1">
                                    <span>
                                      {countdown > 0 ? (
                                        <span>Code expires in <strong className="text-[#134DA7]">{countdown}s</strong></span>
                                      ) : (
                                        <span className="text-amber-600">Code expired?</span>
                                      )}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={!canResend || resendLoading}
                                      onClick={handleResendOtp}
                                      className={`flex items-center gap-1 font-bold transition-colors ${
                                        canResend && !resendLoading 
                                          ? "text-[#134DA7] hover:underline cursor-pointer" 
                                          : "text-gray-400 cursor-not-allowed"
                                      }`}
                                    >
                                      <FaRedo size={11} className={resendLoading ? "animate-spin" : ""} />
                                      <span>{resendLoading ? "Sending..." : "Resend Code"}</span>
                                    </button>
                                  </div>

                                  <button
                                    type="submit"
                                    disabled={otpLoading || otp.length !== 6}
                                    className="w-full py-3.5 text-white font-bold tracking-wide text-base bg-[#134DA7] hover:bg-[#0f3d85] rounded-xl shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                  >
                                    {otpLoading ? (
                                      <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Verifying & Registering...</span>
                                      </>
                                    ) : (
                                      "Verify"
                                    )}
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => { setShowOtpModal(false); setError(""); }}
                                    className="w-full py-2.5 text-sm text-gray-500 font-bold hover:text-gray-700 text-center transition-colors"
                                  >
                                    ← Back to Edit Details
                                  </button>
                                </form>
                            </motion.div>
                        )}
                    </div>
                </div>
            </motion.div>
        </div>
        {/* Success Modal */}
        <AnimatePresence>
            {successMessage && (
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                >
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 w-full max-w-md text-center flex flex-col items-center"
                    >
                        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6">
                            <FaCheckCircle className="text-5xl text-green-500" />
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-2">Success!</h3>
                        <p className="text-gray-500 mb-8 font-medium px-4">
                            Your account has been registered successfully.
                        </p>
                        <div className="w-full relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div 
                                initial={{ width: "0%" }}
                                animate={{ width: "100%" }}
                                transition={{ duration: 1.5, ease: "linear" }}
                                className="absolute top-0 left-0 h-full bg-[#134DA7]"
                            />
                        </div>
                        <p className="text-xs text-gray-400 font-medium mt-4 uppercase tracking-wider">
                            Redirecting to Dashboard...
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
  );
};

export default Register;